<template><div><component-combinator component-name="DtProse" /><h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Prose is a container for pre-rendered HTML content that needs sensible typographic defaults — markdown output, CMS pages, AI-generated responses, help articles, or any block of &quot;raw&quot; HTML that was not authored with Dialtone components, styles, or utility classes.</p>
<p class="d-docsite--paragraph">Style is stripped down and scoped so that elements start from a clean slate. Prose restores those defaults within a scoped container, so content reads naturally without requiring classes on any inner element.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Wrap rendered markdown, CMS output, or any pre-rendered HTML that you do not control.</li>
<li class="d-docsite--list-element">Use for long-form prose-like content blocks: help articles, changelogs, etc.</li>
<li class="d-docsite--list-element">Let Prose handle all inner styling — pass plain HTML with no classes or inline styles.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Wrap Dialtone components or interactive UI — Prose is purely for static and raw HTML structures.</li>
<li class="d-docsite--list-element">Add <code v-pre>class</code> or <code v-pre>style</code> attributes to elements inside Prose — they will be stripped.</li>
<li class="d-docsite--list-element">Place form elements (<code v-pre>&lt;input&gt;</code>, <code v-pre>&lt;select&gt;</code>, <code v-pre>&lt;button&gt;</code>) inside Prose — use proper Dialtone form components instead.</li>
<li class="d-docsite--list-element">Nest a Prose container inside another Prose container.</li>
<li class="d-docsite--list-element">Use Prose as a general-purpose &quot;reset&quot; — it is specifically designed for article-like content.</li>
</ul>
</template>
</dialtone-usage>
<h3 id="content-validation" tabindex="-1" class="d-docsite--header-3">Content validation <a class="header-anchor d-link d-docsite--link d-link" href="#content-validation" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">DtProse enforces that slot content stays &quot;pure&quot; HTML:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><strong>Disallowed elements</strong> — Form controls (<code v-pre>&lt;input&gt;</code>, <code v-pre>&lt;select&gt;</code>, <code v-pre>&lt;textarea&gt;</code>, <code v-pre>&lt;button&gt;</code>, etc.) and custom elements (any hyphenated tag name like <code v-pre>&lt;dt-button&gt;</code>) produce a <code v-pre>console.error</code>. The one exception is <code v-pre>&lt;input type=&quot;checkbox&quot;&gt;</code> inside <code v-pre>&lt;li&gt;</code> for task-list patterns.</li>
<li class="d-docsite--list-element"><strong>Dialtone components</strong> — Any Vue component inside the slot will have their attributes stripped and trigger a console error. DtProse is designed for <strong>plain HTML only</strong> — use Dialtone components outside of Prose.</li>
<li class="d-docsite--list-element"><strong>Attribute stripping</strong> — On elements inside the slot, non-essential attributes (<code v-pre>class</code>, <code v-pre>style</code>, <code v-pre>data-*</code>, event handlers) are silently removed. Structural and accessibility attributes (<code v-pre>id</code>, <code v-pre>href</code>, <code v-pre>src</code>, <code v-pre>alt</code>, <code v-pre>scope</code>, <code v-pre>lang</code>, <code v-pre>dir</code>, etc.) are preserved. Attributes on <code v-pre>&lt;dt-prose&gt;</code> itself are unaffected.</li>
</ul>
<p class="d-docsite--paragraph">This is a development-time guardrail, not a security boundary. XSS protection is the responsibility of whoever produces the HTML.</p>
<h3 id="supported-elements" tabindex="-1" class="d-docsite--header-3">Supported elements <a class="header-anchor d-link d-docsite--link d-link" href="#supported-elements" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Prose styles the full set of content HTML elements:</p>
<table>
<thead>
<tr>
<th>Category</th>
<th>Elements</th>
</tr>
</thead>
<tbody>
<tr>
<td>Headings</td>
<td><code v-pre>h1</code> – <code v-pre>h6</code>, with <code v-pre>&lt;small&gt;</code> subtext</td>
</tr>
<tr>
<td>Text blocks</td>
<td><code v-pre>p</code>, <code v-pre>blockquote</code>, <code v-pre>pre</code>, <code v-pre>hr</code></td>
</tr>
<tr>
<td>Inline formatting</td>
<td><code v-pre>strong</code>, <code v-pre>em</code>, <code v-pre>code</code>, <code v-pre>kbd</code>, <code v-pre>mark</code>, <code v-pre>small</code>, <code v-pre>sub</code>, <code v-pre>sup</code>, <code v-pre>abbr</code>, <code v-pre>var</code>, <code v-pre>samp</code>, <code v-pre>dfn</code>, <code v-pre>del</code>, <code v-pre>ins</code>, <code v-pre>s</code>, <code v-pre>u</code>, <code v-pre>q</code></td>
</tr>
<tr>
<td>Links</td>
<td><code v-pre>a</code> with hover, active, and focus-visible states</td>
</tr>
<tr>
<td>Lists</td>
<td><code v-pre>ul</code>, <code v-pre>ol</code>, <code v-pre>dl</code>/<code v-pre>dt</code>/<code v-pre>dd</code>, task lists</td>
</tr>
<tr>
<td>Tables</td>
<td><code v-pre>table</code>, <code v-pre>thead</code>, <code v-pre>tbody</code>, <code v-pre>th</code>, <code v-pre>td</code>, <code v-pre>caption</code></td>
</tr>
<tr>
<td>Media</td>
<td><code v-pre>img</code>, <code v-pre>figure</code>/<code v-pre>figcaption</code></td>
</tr>
<tr>
<td>Interactive</td>
<td><code v-pre>details</code>/<code v-pre>summary</code></td>
</tr>
<tr>
<td>Code</td>
<td><code v-pre>pre &gt; code</code> blocks, inline <code v-pre>code</code></td>
</tr>
</tbody>
</table>
<h2 id="variants" tabindex="-1" class="d-docsite--header-2">Variants <a class="header-anchor d-link d-docsite--link d-link" href="#variants" aria-hidden="true">#</a></h2>
<h3 id="size" tabindex="-1" class="d-docsite--header-3">Size <a class="header-anchor d-link d-docsite--link d-link" href="#size" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Shift the entire typography scale via the <code v-pre>size</code> prop. Headings, body text, code, and spacing all adjust proportionally.</p>
<code-example only-show="code" source-code='&lt;dt-prose :size=&quot;100&quot;&gt; ... &lt;/dt-prose&gt;
&lt;dt-prose :size=&quot;200&quot;&gt; ... &lt;/dt-prose&gt;
&lt;dt-prose&gt; ... &lt;/dt-prose&gt;'>
<dt-stack data-demo-wrapper gap="400" class="d-w100p">
  <dt-prose :size="100"> ... </dt-prose>
  <dt-prose :size="200"> ... </dt-prose>
  <dt-prose> ... </dt-prose>
</dt-stack>
</code-example>
<h3 id="density" tabindex="-1" class="d-docsite--header-3">Density <a class="header-anchor d-link d-docsite--link d-link" href="#density" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Control line-height independently from size via the <code v-pre>density</code> prop. Useful for tuning readability in different contexts.</p>
<code-example only-show="code" source-code='&lt;dt-prose :density=&quot;100&quot;&gt; ... &lt;/dt-prose&gt;
&lt;dt-prose&gt; ... &lt;/dt-prose&gt;
&lt;dt-prose :density=&quot;300&quot;&gt; ... &lt;/dt-prose&gt;'>
<dt-stack data-demo-wrapper gap="400" class="d-w100p">
  <dt-prose :density="100"> ... </dt-prose>
  <dt-prose> ... </dt-prose>
  <dt-prose :density="300"> ... </dt-prose>
</dt-stack>
</code-example>
<h2 id="examples" tabindex="-1" class="d-docsite--header-2">Examples <a class="header-anchor d-link d-docsite--link d-link" href="#examples" aria-hidden="true">#</a></h2>
<h3 id="headings-and-paragraphs" tabindex="-1" class="d-docsite--header-3">Headings and paragraphs <a class="header-anchor d-link d-docsite--link d-link" href="#headings-and-paragraphs" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">All six heading levels maintain a clear visual hierarchy with balanced spacing between sections, making long documents easy to scan.</p>
<code-example source-code='&lt;dt-prose&gt;
  &lt;h1&gt;Heading Level 1&lt;/h1&gt;
  &lt;p&gt;This is a standard paragraph of body text. It contains enough words to wrap across multiple lines.&lt;/p&gt;
  &lt;h2&gt;Heading Level 2&lt;/h2&gt;
  &lt;p&gt;This is a standard paragraph of body text. It contains enough words to wrap across multiple lines.&lt;/p&gt;
  &lt;h3&gt;Heading Level 3&lt;/h3&gt;
  &lt;p&gt;This is a standard paragraph of body text. It contains enough words to wrap across multiple lines.&lt;/p&gt;
  &lt;h4&gt;Heading Level 4&lt;/h4&gt;
  &lt;p&gt;This is a standard paragraph of body text. It contains enough words to wrap across multiple lines.&lt;/p&gt;
  &lt;h5&gt;Heading Level 5&lt;/h5&gt;
  &lt;p&gt;This is a standard paragraph of body text.&lt;/p&gt;
&lt;/dt-prose&gt;'>
<div data-demo-wrapper class="d-w100p">
  <dt-prose>
    <h1>Heading Level 1</h1>
    <p>This is a standard paragraph of body text. It contains enough words to wrap across multiple lines.</p>
    <h2>Heading Level 2</h2>
    <p>This is a standard paragraph of body text. It contains enough words to wrap across multiple lines.</p>
    <h3>Heading Level 3</h3>
    <p>This is a standard paragraph of body text. It contains enough words to wrap across multiple lines.</p>
    <h4>Heading Level 4</h4>
    <p>This is a standard paragraph of body text. It contains enough words to wrap across multiple lines.</p>
    <h5>Heading Level 5</h5>
    <p>This is a standard paragraph of body text.</p>
  </dt-prose>
</div>
</code-example>
<h3 id="inline-elements" tabindex="-1" class="d-docsite--header-3">Inline elements <a class="header-anchor d-link d-docsite--link d-link" href="#inline-elements" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Rich inline formatting including bold, italic, code, keyboard shortcuts, highlights, and links are all styled to be visually distinct without disrupting the reading flow.</p>
<code-example source-code='&lt;dt-prose&gt;
  &lt;p&gt;&lt;strong&gt;Bold text&lt;/strong&gt;, &lt;em&gt;italic text&lt;/em&gt;, &lt;strong&gt;&lt;em&gt;bold italic&lt;/em&gt;&lt;/strong&gt;, and &lt;u&gt;underlined text&lt;/u&gt;. Here is &lt;s&gt;strikethrough&lt;/s&gt; for deleted content.&lt;/p&gt;
  &lt;p&gt;Here is &lt;a href=&quot;#&quot;&gt;a standard link&lt;/a&gt; and a &lt;a href=&quot;#&quot;&gt;link with &lt;strong&gt;bold&lt;/strong&gt; inside&lt;/a&gt;.&lt;/p&gt;
  &lt;p&gt;&lt;code&gt;inline code&lt;/code&gt;, &lt;kbd&gt;Ctrl&lt;/kbd&gt; + &lt;kbd&gt;S&lt;/kbd&gt;, &lt;mark&gt;highlighted&lt;/mark&gt;, &lt;small&gt;small&lt;/small&gt;, &lt;sub&gt;subscript&lt;/sub&gt; and &lt;sup&gt;superscript&lt;/sup&gt;, and &lt;abbr title=&quot;Abbreviation&quot;&gt;abbr&lt;/abbr&gt;.&lt;/p&gt;
  &lt;p&gt;&lt;var&gt;x&lt;/var&gt; = &lt;var&gt;y&lt;/var&gt; + &lt;var&gt;z&lt;/var&gt; for variables, &lt;samp&gt;Error: file not found&lt;/samp&gt; for sample output, &lt;dfn&gt;definition&lt;/dfn&gt; for terms, and &lt;q&gt;an inline quotation&lt;/q&gt;.&lt;/p&gt;
&lt;/dt-prose&gt;'>
<div data-demo-wrapper class="d-w100p">
  <dt-prose>
    <p><strong>Bold text</strong>, <em>italic text</em>, <strong><em>bold italic</em></strong>, and <u>underlined text</u>. Here is <s>strikethrough</s> for deleted content.</p>
    <p>Here is <a href="#">a standard link</a> and a <a href="#">link with <strong>bold</strong> inside</a>.</p>
    <p><code>inline code</code>, <kbd>Ctrl</kbd> + <kbd>S</kbd>, <mark>highlighted</mark>, <small>small</small>, <sub>subscript</sub> and <sup>superscript</sup>, and <abbr title="Abbreviation">abbr</abbr>.</p>
    <p><var>x</var> = <var>y</var> + <var>z</var> for variables, <samp>Error: file not found</samp> for sample output, <dfn>definition</dfn> for terms, and <q>an inline quotation</q>.</p>
  </dt-prose>
</div>
</code-example>
<h3 id="unordered-list" tabindex="-1" class="d-docsite--header-3">Unordered list <a class="header-anchor d-link d-docsite--link d-link" href="#unordered-list" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Unordered lists support nesting, each with a distinct marker style (disc, circle, square) so readers can track depth at a glance.</p>
<code-example source-code='&lt;dt-prose&gt;
  &lt;ul&gt;
    &lt;li&gt;First item in an unordered list&lt;/li&gt;
    &lt;li&gt;Second item with more text to test wrapping&lt;/li&gt;
    &lt;li&gt;Third item with nested list:
      &lt;ul&gt;
        &lt;li&gt;Nested item one&lt;/li&gt;
        &lt;li&gt;Nested item two&lt;/li&gt;
        &lt;li&gt;Deeply nested:
          &lt;ul&gt;
            &lt;li&gt;Third level item&lt;/li&gt;
          &lt;/ul&gt;
        &lt;/li&gt;
      &lt;/ul&gt;
    &lt;/li&gt;
    &lt;li&gt;Fourth item back at the top level&lt;/li&gt;
  &lt;/ul&gt;
&lt;/dt-prose&gt;'>
<div data-demo-wrapper class="d-w100p">
  <dt-prose>
    <ul>
      <li>First item in an unordered list</li>
      <li>Second item with more text to test wrapping</li>
      <li>Third item with nested list:
        <ul>
          <li>Nested item one</li>
          <li>Nested item two</li>
          <li>Deeply nested:
            <ul>
              <li>Third level item</li>
            </ul>
          </li>
        </ul>
      </li>
      <li>Fourth item back at the top level</li>
    </ul>
  </dt-prose>
</div>
</code-example>
<h3 id="ordered-list" tabindex="-1" class="d-docsite--header-3">Ordered list <a class="header-anchor d-link d-docsite--link d-link" href="#ordered-list" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Ordered lists use decimal numbering with lower-alpha and lower-roman for nested levels, preserving sequence clarity in step-by-step instructions.</p>
<code-example source-code='&lt;dt-prose&gt;
  &lt;ol&gt;
    &lt;li&gt;First step in a process&lt;/li&gt;
    &lt;li&gt;Second step with more detail to test line wrapping&lt;/li&gt;
    &lt;li&gt;Third step with a nested ordered list:
      &lt;ol&gt;
        &lt;li&gt;Sub-step one&lt;/li&gt;
        &lt;li&gt;Sub-step two&lt;/li&gt;
      &lt;/ol&gt;
    &lt;/li&gt;
    &lt;li&gt;Fourth step back at the top level&lt;/li&gt;
  &lt;/ol&gt;
&lt;/dt-prose&gt;'>
<div data-demo-wrapper class="d-w100p">
  <dt-prose>
    <ol>
      <li>First step in a process</li>
      <li>Second step with more detail to test line wrapping</li>
      <li>Third step with a nested ordered list:
        <ol>
          <li>Sub-step one</li>
          <li>Sub-step two</li>
        </ol>
      </li>
      <li>Fourth step back at the top level</li>
    </ol>
  </dt-prose>
</div>
</code-example>
<h3 id="mixed-list" tabindex="-1" class="d-docsite--header-3">Mixed list <a class="header-anchor d-link d-docsite--link d-link" href="#mixed-list" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Unordered and ordered lists can be freely nested inside each other, with each level retaining its own marker style.</p>
<code-example source-code='&lt;dt-prose&gt;
  &lt;ul&gt;
    &lt;li&gt;Unordered item with an ordered sub-list:
      &lt;ol&gt;
        &lt;li&gt;Ordered sub-item one&lt;/li&gt;
        &lt;li&gt;Ordered sub-item two&lt;/li&gt;
      &lt;/ol&gt;
    &lt;/li&gt;
    &lt;li&gt;Another unordered item&lt;/li&gt;
  &lt;/ul&gt;
&lt;/dt-prose&gt;'>
<div data-demo-wrapper class="d-w100p">
  <dt-prose>
    <ul>
      <li>Unordered item with an ordered sub-list:
        <ol>
          <li>Ordered sub-item one</li>
          <li>Ordered sub-item two</li>
        </ol>
      </li>
      <li>Another unordered item</li>
    </ul>
  </dt-prose>
</div>
</code-example>
<h3 id="blockquote" tabindex="-1" class="d-docsite--header-3">Blockquote <a class="header-anchor d-link d-docsite--link d-link" href="#blockquote" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Blockquotes are visually offset with an inline-start border and muted color, supporting multi-paragraph content, nesting, and attributed citations.</p>
<code-example source-code='&lt;dt-prose&gt;
  &lt;blockquote&gt;
    &lt;p&gt;This is a single-paragraph blockquote.&lt;/p&gt;
  &lt;/blockquote&gt;
  &lt;blockquote&gt;
    &lt;p&gt;This is a multi-paragraph blockquote. The first paragraph introduces the idea.&lt;/p&gt;
    &lt;p&gt;The second paragraph continues the thought.&lt;/p&gt;
  &lt;/blockquote&gt;
  &lt;blockquote&gt;
    &lt;p&gt;Good design is as little design as possible.&lt;/p&gt;
    &lt;footer&gt;— &lt;cite&gt;Dieter Rams&lt;/cite&gt;, Ten Principles of Good Design&lt;/footer&gt;
  &lt;/blockquote&gt;
&lt;/dt-prose&gt;'>
<div data-demo-wrapper class="d-w100p">
  <dt-prose>
    <blockquote>
      <p>This is a single-paragraph blockquote.</p>
    </blockquote>
    <blockquote>
      <p>This is a multi-paragraph blockquote. The first paragraph introduces the idea.</p>
      <p>The second paragraph continues the thought.</p>
    </blockquote>
    <blockquote>
      <p>Good design is as little design as possible.</p>
      <footer>— <cite>Dieter Rams</cite>, Ten Principles of Good Design</footer>
    </blockquote>
  </dt-prose>
</div>
</code-example>
<h3 id="code-blocks" tabindex="-1" class="d-docsite--header-3">Code blocks <a class="header-anchor d-link d-docsite--link d-link" href="#code-blocks" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Preformatted code blocks preserve whitespace and use a monospace font on a subtle background, clearly separating code from surrounding prose.</p>
<code-example source-code='  &lt;dt-prose&gt;
    &lt;pre&gt;&lt;code&gt;function greet(name) {
  return `Hello, ${name}!`;
}
const message = greet(&#39;world&#39;);
console.log(message);&lt;/code&gt;&lt;/pre&gt;
  &lt;/dt-prose&gt;'>
<div data-demo-wrapper class="d-w100p">
  <dt-prose>
    <pre><code>function greet(name) {
  return `Hello, ${name}!`;
}
const message = greet('world');
console.log(message);</code></pre>
  </dt-prose>
</div>
</code-example>
<h3 id="table" tabindex="-1" class="d-docsite--header-3">Table <a class="header-anchor d-link d-docsite--link d-link" href="#table" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Prose tables are fully styled with borders, header separation, and row-scoped headers.</p>
<code-example source-code='&lt;dt-prose&gt;
  &lt;table&gt;
    &lt;caption&gt;Layout token scale&lt;/caption&gt;
    &lt;thead&gt;
      &lt;tr&gt;
        &lt;th&gt;Token&lt;/th&gt;
        &lt;th&gt;Value&lt;/th&gt;
        &lt;th&gt;Pixels&lt;/th&gt;
      &lt;/tr&gt;
    &lt;/thead&gt;
    &lt;tbody&gt;
      &lt;tr&gt;
        &lt;th scope=&quot;row&quot;&gt;&lt;code&gt;--dt-layout-100&lt;/code&gt;&lt;/th&gt;
        &lt;td&gt;4rem&lt;/td&gt;
        &lt;td&gt;64px&lt;/td&gt;
      &lt;/tr&gt;
      &lt;tr&gt;
        &lt;th scope=&quot;row&quot;&gt;&lt;code&gt;--dt-layout-200&lt;/code&gt;&lt;/th&gt;
        &lt;td&gt;8rem&lt;/td&gt;
        &lt;td&gt;128px&lt;/td&gt;
      &lt;/tr&gt;
      &lt;tr&gt;
        &lt;th scope=&quot;row&quot;&gt;&lt;code&gt;--dt-layout-400&lt;/code&gt;&lt;/th&gt;
        &lt;td&gt;16rem&lt;/td&gt;
        &lt;td&gt;256px&lt;/td&gt;
      &lt;/tr&gt;
    &lt;/tbody&gt;
  &lt;/table&gt;
&lt;/dt-prose&gt;'>
<div data-demo-wrapper class="d-w100p">
  <dt-prose>
    <table>
      <caption>Layout token scale</caption>
      <thead>
        <tr>
          <th>Token</th>
          <th>Value</th>
          <th>Pixels</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th scope="row"><code>--dt-layout-100</code></th>
          <td>4rem</td>
          <td>64px</td>
        </tr>
        <tr>
          <th scope="row"><code>--dt-layout-200</code></th>
          <td>8rem</td>
          <td>128px</td>
        </tr>
        <tr>
          <th scope="row"><code>--dt-layout-400</code></th>
          <td>16rem</td>
          <td>256px</td>
        </tr>
      </tbody>
    </table>
  </dt-prose>
</div>
</code-example>
<h3 id="definition-list" tabindex="-1" class="d-docsite--header-3">Definition list <a class="header-anchor d-link d-docsite--link d-link" href="#definition-list" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Definition lists pair terms with descriptions, useful for glossaries, metadata tables, and structured key-value content.</p>
<code-example source-code='&lt;dt-prose&gt;
  &lt;dl&gt;
    &lt;dt&gt;Design token&lt;/dt&gt;
    &lt;dd&gt;A named value that represents a design decision, such as a color, spacing unit, or font size.&lt;/dd&gt;
    &lt;dt&gt;Component&lt;/dt&gt;
    &lt;dd&gt;A reusable UI element that encapsulates structure, style, and behavior.&lt;/dd&gt;
    &lt;dt&gt;Utility class&lt;/dt&gt;
    &lt;dd&gt;A single-purpose CSS class that applies one specific style rule.&lt;/dd&gt;
  &lt;/dl&gt;
&lt;/dt-prose&gt;'>
<div data-demo-wrapper class="d-w100p">
  <dt-prose>
    <dl>
      <dt>Design token</dt>
      <dd>A named value that represents a design decision, such as a color, spacing unit, or font size.</dd>
      <dt>Component</dt>
      <dd>A reusable UI element that encapsulates structure, style, and behavior.</dd>
      <dt>Utility class</dt>
      <dd>A single-purpose CSS class that applies one specific style rule.</dd>
    </dl>
  </dt-prose>
</div>
</code-example>
<h3 id="details-and-summary" tabindex="-1" class="d-docsite--header-3">Details and summary <a class="header-anchor d-link d-docsite--link d-link" href="#details-and-summary" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Collapsible sections useful for FAQs, supplementary information, or long reference content.</p>
<code-example source-code='&lt;dt-prose&gt;
  &lt;details&gt;
    &lt;summary&gt;Click to expand for more information&lt;/summary&gt;
    &lt;p&gt;This content is hidden by default and revealed when the user interacts with the summary element.&lt;/p&gt;
    &lt;ul&gt;
      &lt;li&gt;Detail item one&lt;/li&gt;
      &lt;li&gt;Detail item two&lt;/li&gt;
    &lt;/ul&gt;
  &lt;/details&gt;
&lt;/dt-prose&gt;'>
<div data-demo-wrapper class="d-w100p">
  <dt-prose>
    <details>
      <summary>Click to expand for more information</summary>
      <p>This content is hidden by default and revealed when the user interacts with the summary element.</p>
      <ul>
        <li>Detail item one</li>
        <li>Detail item two</li>
      </ul>
    </details>
  </dt-prose>
</div>
</code-example>
<h3 id="task-list" tabindex="-1" class="d-docsite--header-3">Task list <a class="header-anchor d-link d-docsite--link d-link" href="#task-list" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Checkboxes inside list items are the one exception to the &quot;no form elements&quot; rule, supporting the common markdown task-list pattern.</p>
<code-example source-code='&lt;dt-prose&gt;
  &lt;ul&gt;
    &lt;li&gt;&lt;input type=&quot;checkbox&quot; checked disabled&gt; Design review complete&lt;/li&gt;
    &lt;li&gt;&lt;input type=&quot;checkbox&quot; checked disabled&gt; Implementation merged&lt;/li&gt;
    &lt;li&gt;&lt;input type=&quot;checkbox&quot; disabled&gt; Documentation published&lt;/li&gt;
  &lt;/ul&gt;
&lt;/dt-prose&gt;'>
<div data-demo-wrapper class="d-w100p">
  <dt-prose>
    <ul>
      <li><input type="checkbox" checked disabled> Design review complete</li>
      <li><input type="checkbox" checked disabled> Implementation merged</li>
      <li><input type="checkbox" disabled> Documentation published</li>
    </ul>
  </dt-prose>
</div>
</code-example>
<h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Prose renders as a plain <code v-pre>&lt;div&gt;</code> with no implicit ARIA role. Add <code v-pre>role=&quot;article&quot;</code> or wrap in an <code v-pre>&lt;article&gt;</code> element upstream if the content represents a self-contained composition.</li>
<li class="d-docsite--list-element">All heading levels (<code v-pre>h1</code>–<code v-pre>h6</code>) maintain their native semantics and document outline contribution.</li>
<li class="d-docsite--list-element">Links receive visible focus indicators via <code v-pre>box-shadow: var(--dt-shadow-focus)</code>.</li>
<li class="d-docsite--list-element">Abbreviations (<code v-pre>&lt;abbr title=&quot;...&quot;&gt;</code>) display a dotted underline and <code v-pre>cursor: help</code>.</li>
<li class="d-docsite--list-element">Tables use semantic <code v-pre>&lt;thead&gt;</code>, <code v-pre>&lt;th scope&gt;</code>, and visible borders for screen-reader navigability.</li>
</ul>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="prose" /></div></template>


<script setup>
import { DtProse } from '@dialpad/dialtone-vue';
</script>